import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import SmoothScroller from './components/SmoothScroller';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import { isAuthed } from './lib/api';
import PresenceHeartbeat from './components/PresenceHeartbeat';
import ProductUpdateNotice from './components/ProductUpdateNotice';

// §9.4 — heavy app surfaces are code-split off the marketing path
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Builder = lazy(() => import('./pages/Builder'));
const Admin = lazy(() => import('./pages/Admin'));
const Settings = lazy(() => import('./pages/Settings'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Refund = lazy(() => import('./pages/Refund'));
const Updates = lazy(() => import('./pages/Updates'));
const UpdateDetail = lazy(() => import('./pages/UpdateDetail'));

function RequireAuth({ children }: { children: ReactNode }) {
  if (!isAuthed()) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Legacy /builder/:id links (bookmarks, old history rows) → /projects/:id */
function LegacyBuilderRedirect() {
  const { id = '' } = useParams();
  return <Navigate to={`/projects/${id}`} replace />;
}

const fallback = (
  <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: 'var(--ink-mute)' }}>
    Loading…
  </div>
);

export default function App() {
  return (
    <>
      <PresenceHeartbeat />
      <ProductUpdateNotice />
      <Suspense fallback={fallback}>
        <Routes>
          {/* Lenis smooth-scroll is a LANDING-ONLY cinematic (§8). Mounting it
              app-wide hijacks the wheel on dashboard/builder, whose panes scroll
              via inner overflow divs Lenis doesn't drive — wheel goes dead. */}
          <Route path="/" element={<SmoothScroller><Landing /></SmoothScroller>} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/terms-of-service" element={<Terms />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/refund-policy" element={<Refund />} />
          <Route path="/updates" element={<Updates />} />
          <Route path="/updates/:slug" element={<UpdateDetail />} />
          <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
          <Route path="/projects/:projectId" element={<RequireAuth><Builder /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
          <Route path="/builder/:id" element={<LegacyBuilderRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
